import React, { useCallback } from 'react';
import { Container } from '@/components/ui/Container';
import { useSearchParams } from 'react-router-dom';
import { Image } from 'antd';
import MilestonesTabLayout from '@/components/module/projects/milestone_tab/MilestonesTabLayout';
import MilestoneModal from '@/components/module/projects/milestone_tab/MilestoneModal';
import MilestoneLinkModal from '@/components/module/projects/milestone_tab/MilestoneLinkModal';
import PhaseModal from '@/components/module/projects/milestone_tab/PhaseModal';
import { useProjectMilestonesTab } from '@hooks/projects/useProjectMilestones';
import {
    handleAttachmentItemClick,
    handleMarkdownLinkClick,
} from '@hooks/projects/detail/useProjectDetail';
import {
    getMilestoneAttachmentList,
    getPhaseAttachmentList,
    resolveAttachmentUrl,
} from '@hooks/projects/projectHelpers';

export default function ProjectMilestonesTab({ projectId, project }) {
    const {
        project: projectData,
        phaseTabs,
        selectedPhaseId,
        setSelectedPhaseId,
        selectedPhase,
        milestonesForPhase,
        selectedMilestoneId,
        setSelectedMilestoneId,
        selectedMilestone,
        canCreateMilestone,
        createMilestoneDisabled,
        createMilestoneDisabledTooltip,
        canEditMilestone,
        canDeleteMilestone,
        canCreatePhase,
        canEditPhase,
        canDeletePhase,
        canViewPayments,
        openPhaseModal,
        openEditPhaseModal,
        phaseModalOpen,
        phaseModalMode,
        closePhaseModal,
        phaseForm,
        handlePhaseModalSubmit,
        handleDeletePhase,
        deletePhasePending,
        createPhasePending,
        updatePhasePending,
        phasePendingAttachments,
        phasePendingLinkAttachments,
        handlePhasePostFilesSelected,
        removePhasePendingAttachment,
        handlePhaseLinkRequest,
        phaseLinkModalOpen,
        closePhaseLinkModal,
        phaseLinkModalForm,
        handlePhaseLinkModalSubmit,
        openMilestoneModal,
        openEditMilestoneModal,
        milestoneModalMode,
        milestonePhaseOptions,
        milestoneStatusOptions,
        editMilestoneForm,
        handleEditMilestoneSubmit,
        updateMilestonePending,
        handleDeleteMilestone,
        deleteMilestonePending,
        handleEditPostFilesSelected,
        removeEditPendingPostAttachment,
        editDueDateRules,
        setLiveEditDescription,
        editPendingPostAttachments,
        editPendingLinkAttachments,
        editExistingAttachments,
        editMilestoneAttachmentSections,
        handleRemoveExistingAttachment,
        mentionUsers,
        mentionMilestones,
        mentionSprints,
        milestoneModalOpen,
        closeMilestoneModalWithCleanup,
        milestoneForm,
        dueDateRules,
        disabledDateForPicker,
        createPending,
        handleCreateMilestoneAction,
        pendingPostAttachments,
        pendingLinkAttachments,
        linkModalOpen,
        closeLinkModal,
        linkModalForm,
        handleLinkRequest,
        handleLinkModalSubmit,
        handlePostFilesSelected,
        removePendingPostAttachment,
        previewImageUrl,
        setPreviewImageUrl,
        handleAddMilestoneDeliverable,
        handleToggleMilestoneDeliverable,
        handleEditMilestoneDeliverable,
        handleDeleteMilestoneDeliverable,
        createDeliverablePending,
        updateDeliverablePending,
        editDeliverablePending,
        deleteDeliverablePending,
        handleRemoveMilestoneAttachment,
        removePendingLinkAttachment,
        removeEditPendingLinkAttachment,
        removePhasePendingLinkAttachment,
    } = useProjectMilestonesTab(projectId, project);

    const phaseSubmitPending = phaseModalMode === 'edit' ? updatePhasePending : createPhasePending;

    const [searchParams] = useSearchParams();
    const isPhasesOnlyMode = searchParams.get('tab') === 'phases' || searchParams.get('tab') === 'phase';

    const usePhases = project?.capabilities?.usePhases ?? true;
    const useMilestones = project?.capabilities?.useMilestones ?? true;

    const milestoneAttachments = selectedMilestone ? getMilestoneAttachmentList(selectedMilestone) : [];
    const phaseAttachments = selectedPhase ? getPhaseAttachmentList(selectedPhase) : [];

    const handleAttachmentClick = useCallback((att) => {
        handleAttachmentItemClick(att, setPreviewImageUrl, resolveAttachmentUrl);
    }, [setPreviewImageUrl]);

    const handleMilestoneMarkdownLinkClick = useCallback(
        (args) => {
            handleMarkdownLinkClick(args, milestoneAttachments, handleAttachmentClick);
        },
        [milestoneAttachments, handleAttachmentClick],
    );

    const handlePhaseMarkdownLinkClick = useCallback(
        (args) => {
            handleMarkdownLinkClick(args, phaseAttachments, handleAttachmentClick);
        },
        [phaseAttachments, handleAttachmentClick],
    );

    return (
        <>
            <Container $display="flex" $flexDirection="column" $flex="1" $minHeight="0" $overflow="hidden" >
                <MilestonesTabLayout
                    usePhases={usePhases}
                    useMilestones={isPhasesOnlyMode ? false : useMilestones}
                    isPhasesOnlyMode={isPhasesOnlyMode}
                    project={projectData}
                    phaseTabs={phaseTabs}
                    selectedPhaseId={selectedPhaseId}
                    setSelectedPhaseId={setSelectedPhaseId}
                    selectedPhase={selectedPhase}
                    milestonesForPhase={milestonesForPhase}
                    selectedMilestoneId={selectedMilestoneId}
                    setSelectedMilestoneId={setSelectedMilestoneId}
                    selectedMilestone={selectedMilestone}
                    canCreateMilestone={canCreateMilestone}
                    createMilestoneDisabled={createMilestoneDisabled}
                    createMilestoneDisabledTooltip={createMilestoneDisabledTooltip}
                    canEditMilestone={canEditMilestone}
                    canViewPayments={canViewPayments}
                    canDeleteMilestone={canDeleteMilestone}
                    canCreatePhase={canCreatePhase}
                    canEditPhase={canEditPhase}
                    canDeletePhase={canDeletePhase}
                    openPhaseModal={openPhaseModal}
                    openEditPhaseModal={openEditPhaseModal}
                    onDeletePhase={handleDeletePhase}
                    deletePhasePending={deletePhasePending}
                    openMilestoneModal={openMilestoneModal}
                    openEditMilestoneModal={openEditMilestoneModal}
                    onAddMilestoneDeliverable={handleAddMilestoneDeliverable}
                    onToggleMilestoneDeliverable={handleToggleMilestoneDeliverable}
                    onEditMilestoneDeliverable={handleEditMilestoneDeliverable}
                    onDeleteMilestoneDeliverable={handleDeleteMilestoneDeliverable}
                    createDeliverablePending={createDeliverablePending}
                    updateDeliverablePending={updateDeliverablePending}
                    editDeliverablePending={editDeliverablePending}
                    deleteDeliverablePending={deleteDeliverablePending}
                    onRemoveMilestoneAttachment={handleRemoveMilestoneAttachment}
                    updateMilestonePending={updateMilestonePending}
                    onDeleteMilestone={handleDeleteMilestone}
                    deleteMilestonePending={deleteMilestonePending}
                    onMilestoneLinkClick={handleMilestoneMarkdownLinkClick}
                    onPhaseLinkClick={handlePhaseMarkdownLinkClick}
                />
            </Container>
            <PhaseModal
                mode={phaseModalMode || 'create'}
                open={phaseModalOpen}
                onCancel={closePhaseModal}
                phaseForm={phaseForm}
                mentionUsers={mentionUsers}
                mentionMilestones={mentionMilestones}
                mentionSprints={mentionSprints}
                handlePhaseFilesSelected={handlePhasePostFilesSelected}
                handlePhaseLinkRequest={handlePhaseLinkRequest}
                phasePendingAttachments={phasePendingAttachments}
                removePhasePendingAttachment={removePhasePendingAttachment}
                phasePendingLinkAttachments={phasePendingLinkAttachments}
                removePhasePendingLinkAttachment={removePhasePendingLinkAttachment}
                selectedPhaseAttachmentSections={
                    phaseModalMode === 'edit' ? editMilestoneAttachmentSections : { files: [], links: [] }
                }
                onRemoveExistingAttachment={
                    phaseModalMode === 'edit' ? handleRemoveExistingAttachment : undefined
                }
                onSubmit={handlePhaseModalSubmit}
                submitPending={phaseSubmitPending}
            />
            <MilestoneModal
                mode={milestoneModalMode}
                open={milestoneModalOpen}
                onCancel={closeMilestoneModalWithCleanup}
                milestoneForm={milestoneForm}
                editMilestoneForm={editMilestoneForm}
                dueDateRules={dueDateRules}
                editDueDateRules={editDueDateRules}
                disabledDateForPicker={disabledDateForPicker}
                phaseOptions={milestonePhaseOptions}
                statusOptions={milestoneStatusOptions}
                mentionUsers={mentionUsers}
                mentionMilestones={mentionMilestones}
                mentionSprints={mentionSprints}
                handlePostFilesSelected={
                    milestoneModalMode === 'edit' ? handleEditPostFilesSelected : handlePostFilesSelected
                }
                handleLinkRequest={handleLinkRequest}
                pendingPostAttachments={
                    milestoneModalMode === 'edit' ? editPendingPostAttachments : pendingPostAttachments
                }
                removePendingPostAttachment={
                    milestoneModalMode === 'edit' ? removeEditPendingPostAttachment : removePendingPostAttachment
                }
                pendingLinkAttachments={
                    milestoneModalMode === 'edit' ? editPendingLinkAttachments : pendingLinkAttachments
                }
                removePendingLinkAttachment={
                    milestoneModalMode === 'edit' ? removeEditPendingLinkAttachment : removePendingLinkAttachment
                }
                selectedMilestoneAttachmentSections={
                    milestoneModalMode === 'edit' ? editMilestoneAttachmentSections : { files: [], links: [] }
                }
                onRemoveExistingAttachment={
                    milestoneModalMode === 'edit' ? handleRemoveExistingAttachment : undefined
                }
                openAttachment={handleAttachmentClick}
                setPreviewImageUrl={setPreviewImageUrl}
                createPending={createPending}
                handleCreateMilestoneAction={handleCreateMilestoneAction}
                updateMilestonePending={updateMilestonePending}
                handleEditMilestoneSubmit={handleEditMilestoneSubmit}
                setLiveEditDescription={setLiveEditDescription}
            />
            <MilestoneLinkModal
                open={linkModalOpen}
                onCancel={closeLinkModal}
                linkModalForm={linkModalForm}
                handleLinkModalSubmit={handleLinkModalSubmit}
            />
            <MilestoneLinkModal
                open={phaseLinkModalOpen}
                onCancel={closePhaseLinkModal}
                linkModalForm={phaseLinkModalForm}
                handleLinkModalSubmit={handlePhaseLinkModalSubmit}
            />
            <Image
                style={{ display: 'none' }}
                src={previewImageUrl || undefined}
                preview={{
                    visible: !!previewImageUrl,
                    src: previewImageUrl || undefined,
                    rootClassName: 'global-image-preview-root',
                    onVisibleChange: (visible) => {
                        if (!visible) setPreviewImageUrl(null);
                    },
                }}
            />
        </>
    );
}
